import {
  Package,
  HandHelping,
  Users,
  UserCog,
  Store,
  User,
  Bell,
  Tag,
  Files,
  ChevronRight,
  Search,
  X,
} from "lucide-react";
import { Fragment, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { ACCESS_RULES, canAccess } from "@/features/auth/access";
import { isClientManager, isClientStoreUser } from "@/features/auth/roles";
import "./MaisPage.css";

function NavCard({
  icon: Icon,
  label,
  description,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button className="nav-card" type="button" onClick={onClick}>
      <Icon className="nav-card__icon" size={20} aria-hidden="true" />
      <div className="nav-card__copy">
        <strong className="nav-card__title">{label}</strong>
        <p>{description}</p>
      </div>
      <ChevronRight
        className="nav-card__chevron"
        size={18}
        aria-hidden="true"
      />
    </button>
  );
}

export function MaisPage() {
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const [search, setSearch] = useState("");
  const searchInput = useRef<HTMLInputElement>(null);

  const isClient = isClientManager(currentUser);
  const isStoreUser = isClientStoreUser(currentUser);
  const isChecker = currentUser?.role === "checker";
  const isSuper = currentUser?.role === "super";
  const isSupplier = currentUser?.role === "supplier";
  const canViewAlerts = canAccess(currentUser, ACCESS_RULES.alerts);
  const canViewProducts = canAccess(currentUser, ACCESS_RULES.catalogProducts);
  const canViewSuppliers = canAccess(
    currentUser,
    ACCESS_RULES.catalogSuppliers,
  );
  const canViewClients = canAccess(currentUser, ACCESS_RULES.catalogClients);
  const canViewStores = canAccess(currentUser, ACCESS_RULES.catalogStores);
  const canViewUsers = canAccess(currentUser, ACCESS_RULES.users);
  const canViewReports = canAccess(currentUser, ACCESS_RULES.reports);

  const title = isClient || isStoreUser ? "Configurações" : "Mais";
  const destinations = [
    {
      group: "Conta",
      icon: User,
      label: "Meu Perfil",
      description: "Senha, PIN e dados da conta",
      keywords: "trocar alterar redefinir senha pin segurança dados pessoais",
      allowed: true,
      onClick: () => navigate({ to: "/perfil" }),
    },
    {
      group: "Operação",
      icon: Bell,
      label: "Notificações",
      description: "Alertas e solicitações",
      keywords: "avisos pendências responder solicitações",
      allowed: canViewAlerts,
      onClick: () => navigate({ to: "/mais/notificacoes" }),
    },
    {
      group: "Operação",
      icon: Package,
      label: "Caixas",
      description: isSupplier
        ? "Saldo e movimentações"
        : "Caixas dos fornecedores",
      keywords: "saldo movimentações caixas fornecedor estoque",
      allowed: isSupplier || isSuper || isChecker,
      onClick: () => navigate({ to: "/mais/caixas" }),
    },
    {
      group: "Operação",
      icon: Files,
      label: "Relatórios",
      description: "Pedidos por fornecedor",
      keywords: "baixar pedidos exportar pdf documentos",
      allowed: canViewReports,
      onClick: () => navigate({ to: "/relatorios" }),
    },
    {
      group: "Cadastros",
      icon: isSupplier ? Tag : Package,
      label: "Produtos",
      description: isSupplier ? "Catálogo de produtos" : "Gestão de produtos",
      keywords: "cadastrar editar atualizar catálogo produtos",
      allowed: canViewProducts,
      onClick: () => navigate({ to: "/mais/produtos" }),
    },
    {
      group: "Cadastros",
      icon: HandHelping,
      label: "Fornecedores",
      description: "Gestão de fornecedores",
      keywords: "cadastrar editar fornecedor",
      allowed: canViewSuppliers,
      onClick: () => navigate({ to: "/mais/fornecedores" }),
    },
    {
      group: "Cadastros",
      icon: Users,
      label: "Clientes",
      description: "Gestão de clientes",
      keywords: "cadastrar editar cliente",
      allowed: canViewClients,
      onClick: () => navigate({ to: "/mais/clientes" }),
    },
    {
      group: "Cadastros",
      icon: Store,
      label: "Lojas",
      description: isStoreUser
        ? "Lojas sob sua responsabilidade"
        : "Gestão de lojas",
      keywords: "cadastrar editar unidade filial filiais loja",
      allowed: canViewStores,
      onClick: () => navigate({ to: "/mais/lojas" }),
    },
    {
      group: "Cadastros",
      icon: UserCog,
      label: "Usuários",
      description: isClient ? "Acessos das lojas" : "Gerenciar acessos",
      keywords: "permissões contas cadastrar usuário acessos",
      allowed: canViewUsers,
      onClick: () => navigate({ to: "/mais/usuarios" }),
    },
  ].filter((destination) => destination.allowed);
  const normalize = (value: string) =>
    value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  const terms = normalize(search).trim().split(/\s+/).filter(Boolean);
  const results = terms.length
    ? destinations.filter((destination) =>
        terms.every((term) =>
          normalize(
            `${destination.label} ${destination.description} ${destination.keywords}`,
          ).includes(term),
        ),
      )
    : destinations;
  const groups = terms.length
    ? ["Resultados"]
    : ["Conta", "Operação", "Cadastros"];

  return (
    <div className="mais-page-shell app-page-frame">
      <h1>{title}</h1>
      <div className="mais-search">
        <label htmlFor="mais-search-input">Buscar funcionalidade</label>
        <div className="mais-search__field">
          <Search size={19} aria-hidden="true" />
          <input
            ref={searchInput}
            id="mais-search-input"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="O que você quer fazer?"
          />
          {search && (
            <button
              type="button"
              aria-label="Limpar busca"
              onClick={() => {
                setSearch("");
                searchInput.current?.focus();
              }}
            >
              <X size={18} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
      {terms.length > 0 && (
        <p className="mais-search__status" role="status">
          {results.length === 0
            ? "Nenhuma funcionalidade encontrada"
            : `${results.length} ${results.length === 1 ? "resultado" : "resultados"}`}
        </p>
      )}
      <div className="mais-directory" data-searching={terms.length > 0}>
        {groups.map((group) => {
          const cards = terms.length
            ? results
            : results.filter((destination) => destination.group === group);
          return cards.length ? (
            <Fragment key={group}>
              <h2 className="mais-directory__group">{group}</h2>
              {cards.map((destination) => (
                <NavCard key={destination.label} {...destination} />
              ))}
            </Fragment>
          ) : null;
        })}
      </div>
    </div>
  );
}
